import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import firebase from '../config/firebase.config';
import _ from 'lodash';
import { DEFAULT_WORKING_DAYS } from '../utils/attendanceUtils';

const database = firebase.firestore();

const DEFAULT_WORKING_SCHEDULE = {
  days: DEFAULT_WORKING_DAYS,
  startTime: '09:30 AM',
  endTime: '06:30 PM',
};

/**
 * Custom hook to fetch and cache the company's workingSchedule setting from
 * companyDatabase. Mirrors useStealthMode's cache-first + onSnapshot pattern.
 *
 * @param {string} [overrideCompanyId] - When set (non-empty), read the schedule for this company instead of the user's active company.
 * @returns {{ workingSchedule: { days: string[], startTime: string, endTime: string }, loading: boolean }}
 */
export function useWorkingSchedule(overrideCompanyId) {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyId = !_.isEmpty(userProfile?.activeCompany)
    ? userProfile?.activeCompany?.companyId
    : null;
  const companyId =
    typeof overrideCompanyId === 'string' && overrideCompanyId.trim() !== ''
      ? overrideCompanyId.trim()
      : activeCompanyId;

  const [workingSchedule, setWorkingSchedule] = useState(
    DEFAULT_WORKING_SCHEDULE
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setWorkingSchedule(DEFAULT_WORKING_SCHEDULE);
      setLoading(false);
      return;
    }

    const cacheKey = `workingSchedule_${companyId}`;
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      try {
        setWorkingSchedule(JSON.parse(cachedValue));
        setLoading(false);
      } catch {
        // Ignore a malformed cache entry - the snapshot below will replace it.
      }
    }

    const companyRef = database.collection('companyDatabase').doc(companyId);

    const unsubscribe = companyRef.onSnapshot(
      (doc) => {
        const schedule = doc.exists
          ? {
              days:
                doc.data()?.workingSchedule?.days ||
                DEFAULT_WORKING_SCHEDULE.days,
              startTime:
                doc.data()?.workingSchedule?.startTime ||
                DEFAULT_WORKING_SCHEDULE.startTime,
              endTime:
                doc.data()?.workingSchedule?.endTime ||
                DEFAULT_WORKING_SCHEDULE.endTime,
            }
          : DEFAULT_WORKING_SCHEDULE;

        // The whole companyDatabase doc re-fires this listener whenever ANY
        // sibling field (stealthMode, screenshot, ...) is saved, not just
        // workingSchedule. Bail out to the same object when the resolved
        // value hasn't actually changed, so consumers that sync local
        // editable state off this value (e.g. an in-progress, unsaved edit)
        // don't get clobbered by an unrelated save elsewhere on the page.
        setWorkingSchedule((prev) =>
          prev.startTime === schedule.startTime &&
          prev.endTime === schedule.endTime &&
          prev.days.length === schedule.days.length &&
          prev.days.every((day, index) => day === schedule.days[index])
            ? prev
            : schedule
        );
        localStorage.setItem(cacheKey, JSON.stringify(schedule));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching workingSchedule:', error);
        if (cachedValue === null) {
          setWorkingSchedule(DEFAULT_WORKING_SCHEDULE);
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [companyId]);

  return { workingSchedule, loading };
}
